/**
 * replaceTextWithHTMLFixed.ts
 *
 * Безопасная замена текста в DOM с вставкой HTML (TypeScript).
 *
 * Подход:
 *  - Для каждой текстовой ноды создаём свежий RegExp с флагом 'g' (и 'u').
 *  - Разбиваем текст ноды на сегменты: plain text / заменённый HTML.
 *  - Собираем DocumentFragment из текстовых узлов и HTML-фрагментов.
 *  - Подменяем оригинальную текстовую ноду на получившийся фрагмент.
 *
 *  Избегаем использования одного глобального RegExp для множества тестов,
 *  чтобы не столкнуться с проблемой lastIndex.
 */

export function replaceTextWithHTML(
	search: string | RegExp,
	htmlReplacement: string,
	root: HTMLElement | Document = document,
): void {
	// Помощник: экранировать строку для RegExp
	const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

	// Создаём RegExpFactory — возвращает свежий RegExp для каждой ноды
	const makeRegExp = (pattern: string, flags: string) => {
		// гарантируем наличие 'g' и 'u'
		if (!flags.includes("g")) flags += "g";
		if (!flags.includes("u")) flags += "u";
		return new RegExp(pattern, flags);
	};

	// Если search — строка, используем экранированный паттерн
	const isRegExp = search instanceof RegExp;
	const pattern = isRegExp
		? (search as RegExp).source
		: escapeRegExp(search as string);
	const userFlags = isRegExp
		? (search as RegExp).flags.replace(/g|u/g, "")
		: ""; // убираем g/u из userFlags, добавим потом

	// TreeWalker для текстовых узлов, игнорируем script/style
	const walker = document.createTreeWalker(
		root,
		NodeFilter.SHOW_TEXT,
		{
			acceptNode(node) {
				const parent = node.parentElement;
				if (!parent) return NodeFilter.FILTER_REJECT;
				const tag = parent.tagName.toLowerCase();
				if (tag === "script" || tag === "style" || tag === "noscript")
					return NodeFilter.FILTER_REJECT;
				return NodeFilter.FILTER_ACCEPT;
			},
		},
		false,
	);

	const nodes: Text[] = [];
	let n: Node | null;
	while ((n = walker.nextNode())) {
		if (n.nodeType === Node.TEXT_NODE) nodes.push(n as Text);
	}

	for (const textNode of nodes) {
		const text = textNode.nodeValue;
		if (!text) continue;

		// свежий RegExp для этой ноды
		const re = makeRegExp(pattern, userFlags);

		// быстро проверим, есть ли вообще совпадения
		if (!re.test(text)) continue;

		// сбрасываем lastIndex и проходим по всем матчам
		re.lastIndex = 0;

		const fragment = document.createDocumentFragment();
		let lastIndex = 0;
		let match: RegExpExecArray | null;

		while ((match = re.exec(text)) !== null) {
			const matchStart = match.index;
			const matchEnd = re.lastIndex;

			// добавляем текст перед матчем
			if (matchStart > lastIndex) {
				fragment.appendChild(
					document.createTextNode(text.slice(lastIndex, matchStart)),
				);
			}

			// создаём узлы из HTML-замены. Используем <template> для парсинга HTML
			const tpl = document.createElement("template");
			// безопасно формируем innerHTML: htmlReplacement может содержать произвольный HTML,
			// мы просто вставляем его в template (внимание: если htmlReplacement содержит <script> — он не выполнится при вставке)
			tpl.innerHTML =
				typeof htmlReplacement === "function"
					? (htmlReplacement as any)(match[0])
					: (htmlReplacement as string);

			// если в replacement нужны группы из match — можно заменить подстановки вручную.
			// Простой способ — подставлять ${0}, ${1} и т.д. Если нужно — расширю.
			fragment.appendChild(tpl.content.cloneNode(true));
			lastIndex = matchEnd;

			// Защита на случай пустого матча (чтобы избежать бесконечного цикла)
			if (matchEnd === re.lastIndex) {
				re.lastIndex++;
			}
		}

		// добавляем хвост после последнего совпадения
		if (lastIndex < text.length) {
			fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
		}

		// Заменяем оригинальную текстовую ноду на собранный фрагмент
		// Если фрагмент пуст (маловероятно) — удаляем ноду
		if (fragment.childNodes.length) {
			textNode.parentNode?.replaceChild(fragment, textNode);
		} else {
			textNode.remove();
		}
	}
}
